import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import ChessBoardView from '@/components/chess/ChessBoardView';
import useChessEngine from '@/hooks/useChessEngine';
import { playChessMoveSound, playChessCaptureSound } from '@/lib/chessSounds';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RotateCcw, Loader2, Bot, User as UserIcon, Trophy, Handshake, Flag, Coins, Repeat, Lock } from 'lucide-react';

/**
 * PC-1 · PC-2 · PC-4: Play with Computer — runner del alumno.
 *   - PC-1: alumno mueve → Stockfish responde (useChessEngine, chess.js, ChessBoardView).
 *   - PC-2: detección de mate y tablas al final de cada jugada; bloqueo de tablero.
 *   - PC-4: intentos y puntos.
 *       Un intento = una partida completa. Mate del alumno → TODOS los puntos.
 *       Tablas o mate del motor → 0 puntos, pero se consume un intento.
 *       Actividad completada al ganar; bloqueada al agotar max_attempts.
 *       El backend valida los SAN reproduciendo la partida con python-chess.
 */

const START = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

function Confetti() {
  const pieces = React.useMemo(() => {
    const colors = ['#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#8b5cf6', '#ef4444', '#facc15'];
    return Array.from({ length: 60 }, (_, i) => ({
      left: Math.random() * 100, delay: Math.random() * 0.6,
      duration: 1.4 + Math.random() * 1.4, rotate: Math.random() * 360,
      color: colors[i % colors.length], size: 6 + Math.random() * 6,
    }));
  }, []);
  return (
    <div data-testid="pc-confetti" className="pointer-events-none absolute inset-0 overflow-hidden">
      <style>{`@keyframes pc-fall {0%{transform:translateY(-20px) rotate(0);opacity:1}100%{transform:translateY(560px) rotate(720deg);opacity:0}}`}</style>
      {pieces.map((p, i) => (
        <span key={i} style={{ position:'absolute', top:-20, left:`${p.left}%`, width:p.size, height:p.size,
          background:p.color, borderRadius:2, transform:`rotate(${p.rotate}deg)`,
          animation:`pc-fall ${p.duration}s ${p.delay}s ease-in forwards` }}/>
      ))}
    </div>
  );
}

export default function PlayComputerPlayer({ open, onClose, activity, studyId, roomId, inline = false }) {
  const { user } = useAuth();
  const isStudent = user?.role === 'student';
  const engine = useChessEngine();
  const gameRef = useRef(null);
  const movesRef = useRef([]);           // SAN moves de la partida en curso
  const submittedRef = useRef(false);    // evita doble envío del mismo intento
  const [fen, setFen] = useState(START);
  const [thinking, setThinking] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // 'checkmate_win' | 'checkmate_loss' | 'draw'
  // PC-4 status persistente del alumno para esta actividad
  const [pc, setPc] = useState(null); // { attempts_used, max_attempts, attempts_left, completed, locked, points_earned }
  // PC-5F: estado de click-to-move (mismo patrón que LivePuzzle para
  // ejercicios normales; NO reemplaza drag & drop — coexisten).
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalDests, setLegalDests] = useState({}); // { dst: { isCapture: bool } }

  const studentSide = activity?.student_side === 'black' ? 'b' : 'w';
  const engineSide = studentSide === 'w' ? 'b' : 'w';
  const level = Number.isFinite(activity?.engine_level) ? activity.engine_level : 5;
  const orientation = studentSide === 'w' ? 'white' : 'black';
  const points = Number.isFinite(activity?.points) ? activity.points : 0;
  const activityId = activity?.activity_id || activity?.id;

  const loadStatus = useCallback(async () => {
    if (!isStudent || !studyId || !activityId) { setPc(null); return; }
    try {
      const statusQuery = roomId ? `?room_id=${encodeURIComponent(roomId)}` : '';
      const { data } = await api.get(
        `/studies/${studyId}/activities/${activityId}/play-computer/status${statusQuery}`
      );
      setPc(data);
    } catch { setPc(null); }
  }, [isStudent, studyId, activityId, roomId]);

  const evaluateEnd = useCallback(() => {
    const c = gameRef.current; if (!c) return false;
    if (typeof c.isCheckmate === 'function' && c.isCheckmate()) {
      const loser = c.turn();
      setResult(loser === studentSide ? 'checkmate_loss' : 'checkmate_win');
      setStatus(''); setThinking(false); return true;
    }
    if (typeof c.isDraw === 'function' && c.isDraw()) {
      setResult('draw'); setStatus(''); setThinking(false); return true;
    }
    return false;
  }, [studentSide]);

  // Reset local (mismo intento, todavía sin enviar)
  const resetPosition = useCallback(() => {
    if (!activity) return;
    const initial = activity.fen || START;
    try {
      const c = new Chess(initial);
      gameRef.current = c;
      movesRef.current = [];
      submittedRef.current = false;
      setFen(c.fen()); setError(''); setResult(null); setThinking(false);
      setSelectedSquare(null); setLegalDests({}); // PC-5F: limpiar selección
      setStatus(c.turn() === studentSide ? 'Tu turno' : 'Calculando…');
    } catch { setError('FEN inválido en la actividad'); gameRef.current = null; }
  }, [activity, studentSide]);

  useEffect(() => {
    if (!open) return;
    resetPosition();
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activity?.id]);

  // PC-5G: espejo visual en tiempo real para el monitor del profesor.
  // No valida la partida ni consume intentos; el endpoint /attempt sigue
  // siendo la fuente de verdad para resultado y puntos.
  const syncClassroomProgress = useCallback(() => {
    if (!roomId || !isStudent || !gameRef.current) return;

    const c = gameRef.current;
    api.post(`/classrooms/${roomId}/play-computer-progress`, {
      fen: c.fen(),
      last_move: movesRef.current[movesRef.current.length - 1] || null,
    }).catch(() => {});
  }, [roomId, isStudent]);

  const playEngineMove = useCallback(async () => {
    const c = gameRef.current;
    if (!c || !engine.ready || result || c.isGameOver?.() || c.turn() !== engineSide) return;
    setThinking(true); setStatus('Calculando…');
    try {
      const uci = await engine.bestMove(c.fen(), { level, movetime: 700 });
      if (!uci || uci === '(none)') { setThinking(false); if (!evaluateEnd()) setStatus('Motor no encontró jugada'); return; }
      const from = uci.slice(0,2), to = uci.slice(2,4), promo = uci.length > 4 ? uci[4] : 'q';
      let mv = null;
      try { mv = c.move({ from, to, promotion: promo }); } catch { mv = null; }
      if (!mv) { setThinking(false); setStatus('Motor devolvió una jugada no aplicable'); return; }
      movesRef.current.push(mv.san);
      setFen(c.fen()); setThinking(false);
      const ended = evaluateEnd();
      syncClassroomProgress();
      if (ended) return;
      setStatus(c.turn() === studentSide ? 'Tu turno' : '');
    } catch { setThinking(false); setError('El motor no respondió'); }
  }, [engine, engineSide, level, studentSide, result, evaluateEnd, syncClassroomProgress]);

  // Al detectar resultado, enviar al backend (una sola vez)
  useEffect(() => {
    if (!result || submittedRef.current || !isStudent || !studyId || !activityId) return;
    submittedRef.current = true;
    (async () => {
      try {
        const { data } = await api.post(
          `/studies/${studyId}/activities/${activityId}/play-computer/attempt`,
          { result, moves: movesRef.current, ...(roomId ? { room_id: roomId } : {}) }
        );
        // El backend devuelve el status actualizado
        if (data && data.attempts_used !== undefined) setPc(data);
      } catch (e) {
        // Si el backend rechaza el resultado (p.ej. discrepancia) recargamos status
        loadStatus();
      }
    })();
  }, [result, isStudent, studyId, activityId, loadStatus]);

  useEffect(() => {
    if (!open || !engine.ready || !gameRef.current || result) return;
    if (gameRef.current.turn() === engineSide && !thinking) playEngineMove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, engine.ready, fen, result]);

  // Bloqueo total si ya está completada o sin intentos
  const activityLocked = !!(pc && pc.locked && isStudent);

  const handleDrop = useCallback((sourceSquare, targetSquare) => {
    const c = gameRef.current;
    if (!c || thinking || result || activityLocked) return false;
    if (c.turn() !== studentSide) return false;
    let mv = null;
    try { mv = c.move({ from: sourceSquare, to: targetSquare, promotion: 'q' }); } catch { mv = null; }
    if (!mv) return false;
    movesRef.current.push(mv.san);
    setFen(c.fen()); setStatus('');
    // PC-5F: cualquier movimiento aceptado (drag o click) limpia la selección
    setSelectedSquare(null); setLegalDests({});
    const ended = evaluateEnd();
    syncClassroomProgress();
    if (ended) return true;
    setTimeout(() => playEngineMove(), 100);
    return true;
  }, [thinking, studentSide, playEngineMove, result, evaluateEnd, activityLocked, syncClassroomProgress]);

  // PC-5F: computa destinos legales para la casilla seleccionada usando la
  // misma API de chess.js que ya utiliza LivePuzzle para ejercicios normales.
  const computeLegalDests = useCallback((sq) => {
    const c = gameRef.current;
    if (!c) return {};
    try {
      const mv = c.moves({ square: sq, verbose: true });
      const dests = {};
      mv.forEach(m => { dests[m.to] = { isCapture: !!(m.flags && (m.flags.includes('c') || m.flags.includes('e'))) }; });
      return dests;
    } catch { return {}; }
  }, []);

  // PC-5F: click-to-move. Mismo patrón exacto que LivePuzzle.handleSquareClick
  // (ejercicios normales). Toda validación real la sigue haciendo chess.js /
  // handleDrop — este handler solo gestiona la SELECCIÓN visual. No se ejecuta
  // ningún movimiento ilegal; los clicks ilegales simplemente re-seleccionan
  // o limpian.
  const handleSquareClick = useCallback((sq) => {
    const c = gameRef.current;
    if (!c || thinking || result || activityLocked) return;
    if (c.turn() !== studentSide) return;
    if (selectedSquare) {
      if (sq === selectedSquare) { setSelectedSquare(null); setLegalDests({}); return; }
      if (legalDests[sq]) {
        // Click-to-move NO pasa por ChessBoardView.handlePieceDrop, así que
        // debemos disparar el sonido manualmente (LivePuzzle usa el mismo
        // patrón). Detección de captura ANTES de mutar el estado.
        let wasCapture = false;
        try {
          const dstPiece = c.get(sq);
          if (dstPiece) wasCapture = true;
          else {
            const srcPiece = c.get(selectedSquare);
            if (srcPiece && srcPiece.type === 'p' && selectedSquare[0] !== sq[0]) wasCapture = true;
          }
        } catch { /* silent — fallback a sonido de movimiento */ }
        const ok = handleDrop(selectedSquare, sq);
        if (ok) (wasCapture ? playChessCaptureSound : playChessMoveSound)();
        return;
      }
      // Cambiar selección a otra pieza propia
      const piece = c.get(sq);
      if (piece && piece.color === studentSide) {
        setSelectedSquare(sq);
        setLegalDests(computeLegalDests(sq));
        return;
      }
      // Click fuera de destinos legales y sin pieza propia → limpiar
      setSelectedSquare(null); setLegalDests({});
      return;
    }
    // Primer click: solo aceptar piezas propias con al menos un destino legal
    const piece = c.get(sq);
    if (!piece || piece.color !== studentSide) return;
    const dests = computeLegalDests(sq);
    if (Object.keys(dests).length === 0) return;
    setSelectedSquare(sq);
    setLegalDests(dests);
  }, [thinking, result, activityLocked, studentSide, selectedSquare, legalDests, handleDrop, computeLegalDests]);

  // PC-5F: hint visuales (selección + destinos legales). Mismo estilo que
  // LivePuzzle para que kids vean exactamente los mismos indicadores en toda
  // la app. Se pasa a ChessBoardView vía prop `extraSquareStyles`.
  const hintStyles = useMemo(() => {
    const s = {};
    if (selectedSquare) {
      s[selectedSquare] = { backgroundColor: 'rgba(255, 236, 100, 0.75)' };
    }
    Object.entries(legalDests).forEach(([dst, meta]) => {
      const base = s[dst] || {};
      s[dst] = {
        ...base,
        background: meta.isCapture
          ? 'radial-gradient(circle, transparent 0 88%, rgba(0,0,0,0.42) 88% 96%, transparent 96% 100%)'
          : 'radial-gradient(circle, rgba(0,0,0,0.28) 0 18%, transparent 18% 100%)',
      };
    });
    return s;
  }, [selectedSquare, legalDests]);

  const activityTitle = activity?.title || 'Play with Computer';
  const goalLabel = activity?.goal === 'draw' ? 'Empatar' : 'Ganar';
  const sideLabel = studentSide === 'w' ? 'Blancas' : 'Negras';
  const movesPlayed = movesRef.current.length;

  // Responsive board size: desktop conserva 480px; en móvil el tablero
  // se adapta al ancho real disponible sin modificar la lógica del juego.
  const boardWrapRef = useRef(null);
  const [boardPx, setBoardPx] = useState(480);

  useEffect(() => {
    const el = boardWrapRef.current;

    const compute = () => {
      const cw = el?.clientWidth || 480;
      const mobile = typeof window !== 'undefined'
        && window.matchMedia('(max-width: 767px)').matches;

      if (mobile) {
        setBoardPx(Math.min(480, Math.max(240, Math.floor(cw))));
        return;
      }

      const availableHeight = inline
        ? window.innerHeight - (el?.getBoundingClientRect().top || 0) - 20
        : 480;

      const maxBoard = inline && !mobile ? 450 : 480;

      setBoardPx(
        Math.min(
          maxBoard,
          Math.max(240, Math.floor(cw)),
          Math.max(240, Math.floor(availableHeight))
        )
      );
    };

    compute();

    let ro;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(compute);
      ro.observe(el);
    }

    window.addEventListener('resize', compute);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, [inline]);

  // Contadores para mostrar (después del intento, pc está actualizado)
  const attemptsUsed = pc?.attempts_used ?? 0;
  const maxAttempts = pc?.max_attempts ?? (activity?.max_attempts ?? 3);
  const attemptsLeft = pc?.attempts_left ?? maxAttempts;
  const completed = !!pc?.completed;
  const pointsEarned = pc?.points_earned ?? 0;
  const attemptsExhausted = isStudent && attemptsLeft === 0;

  // Banner de resultado
  const resultConfig = result === 'checkmate_win'
    ? { testId:'pc-result-win', title:'¡Jaque mate!', sub:'¡Excelente! Has completado la actividad.',
        Icon:Trophy, bg:'bg-emerald-50', border:'border-emerald-300', text:'text-emerald-800', celebrate:true,
        extra: isStudent ? `¡Ganaste ${points} puntos!` : null }
    : result === 'checkmate_loss'
    ? attemptsExhausted
      ? { testId:'pc-result-loss', title:'¡Sigue intentándolo!', sub:'Lo has hecho muy bien.',
          Icon:Trophy, bg:'bg-amber-50', border:'border-amber-300', text:'text-amber-800', celebrate:false,
          extra:'Cada partida te hace mejor.' }
      : { testId:'pc-result-loss', title:'¡Jaque mate!', sub:'La computadora te ha dado mate. Vuelve a intentarlo.',
          Icon:Flag, bg:'bg-rose-50', border:'border-rose-300', text:'text-rose-800', celebrate:false,
          extra: isStudent ? '0 puntos' : null }
    : result === 'draw'
    ? attemptsExhausted
      ? { testId:'pc-result-draw', title:'¡Sigue intentándolo!', sub:'Lo has hecho muy bien.',
          Icon:Trophy, bg:'bg-amber-50', border:'border-amber-300', text:'text-amber-800', celebrate:false,
          extra:'Cada partida te hace mejor.' }
      : { testId:'pc-result-draw', title:'Partida terminada', sub:'La partida terminó en tablas.',
          Icon:Handshake, bg:'bg-amber-50', border:'border-amber-300', text:'text-amber-800', celebrate:false,
          extra: isStudent ? '0 puntos' : null }
    : null;

  // Panel superior con contadores (solo alumnos)
  const canRetry = isStudent && result && !completed && attemptsLeft > 0;
  const showFinalLock = isStudent && (completed || attemptsLeft === 0) && (activityLocked || result);

  // PC-5E: Extraemos el cuerpo (tablero + paneles + resultado) para poder
  // renderizarlo tanto dentro del Dialog (modo StudyDetail) como INLINE
  // dentro de Classroom, evitando el overlay/backdrop que bloquea el drag
  // y el click en las piezas cuando el runner se abre en la vista del alumno.
  const body = error ? (
    <div data-testid="pc-error" className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 p-3 text-sm">{error}</div>
  ) : (
          <div className="flex flex-col items-center gap-3">
            {/* PC-4: panel superior de estado.
                En Classroom inline, Intentos se muestra junto al título. */}
            {!inline && isStudent && (
              <div className="w-full max-w-[520px] flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1 text-slate-700" data-testid="pc-attempts">
                    <Repeat className="w-3.5 h-3.5"/>
                    Intentos: <b>{Math.min(attemptsUsed, maxAttempts)}/{maxAttempts}</b>
                  </span>
                  <span className="inline-flex items-center gap-1 text-slate-700" data-testid="pc-points-avail">
                    <Coins className="w-3.5 h-3.5 text-amber-500"/>
                    Puntos disponibles: <b>{completed ? pointsEarned : points}</b>
                  </span>
                </div>
                {completed && (
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold" data-testid="pc-completed-badge">
                    <Trophy className="w-3.5 h-3.5"/> Completada
                  </span>
                )}
              </div>
            )}

            {/* Bloqueo final: sin intentos y sin éxito */}
            {isStudent && attemptsLeft === 0 && !completed && !result && (
              <div data-testid="pc-final-locked" className={`w-full max-w-[520px] rounded-xl border border-slate-300 bg-slate-100 text-slate-700 p-3 flex items-center gap-3 ${inline ? 'md:col-span-2' : ''}`}>
                <Lock className="w-5 h-5 shrink-0"/>
                <div className="flex-1">
                  <div className="font-display font-extrabold">Actividad finalizada</div>
                  <div className="text-xs mt-0.5">No conseguiste el mate en tus intentos. 0 puntos.</div>
                </div>
              </div>
            )}

            {!inline && (
              <div className="w-full flex items-center justify-between px-1 md:max-w-[500px] md:absolute md:right-4 md:top-[76px] md:w-auto md:border-t md:border-slate-200 md:pt-3">
              <div className="flex items-center gap-5 md:gap-5">
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  {result ? (
                  <span data-testid="pc-status" className="text-slate-400">Partida finalizada</span>
                ) : !engine.ready ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Cargando motor…</>
                ) : thinking ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin"/> <span data-testid="pc-thinking">Calculando…</span></>
                ) : activityLocked ? (
                  <span data-testid="pc-status" className="text-slate-400">Actividad bloqueada</span>
                ) : (
                  <><UserIcon className="w-3.5 h-3.5 text-emerald-600"/> <span data-testid="pc-status">{status}</span></>
                )}
                </div>

                {inline && isStudent && (
                  <span
                    className="hidden md:inline-flex items-center gap-1 text-xs text-slate-700"
                    data-testid="pc-attempts"
                  >
                    <Repeat className="w-3.5 h-3.5" />
                    Intentos: <b>{Math.min(attemptsUsed, maxAttempts)}/{maxAttempts}</b>
                  </span>
                )}
              </div>

              {/* Reiniciar solo antes de mover; Intentar de nuevo después de un intento fallido */}
              <div className="md:ml-6">
                {canRetry ? (
                  <Button data-testid="pc-retry" size="sm" onClick={resetPosition} className="bg-indigo-600 hover:bg-indigo-700">
                    <Repeat className="w-3.5 h-3.5 mr-1"/> Intentar de nuevo · te quedan {attemptsLeft}
                  </Button>
                ) : (
                  <Button data-testid="pc-reset" size="sm" variant="outline"
                    onClick={resetPosition}
                    disabled={movesPlayed > 0 || activityLocked || !!result}
                    className={result ? 'opacity-50' : ''}
                    title={movesPlayed > 0 && !result ? 'No puedes reiniciar en medio de un intento activo' : 'Reiniciar'}>
                    <RotateCcw className="w-3.5 h-3.5 mr-1"/> Reiniciar
                  </Button>
                )}
              </div>
              </div>
            )}

            {resultConfig && !inline && (
              <div data-testid={resultConfig.testId}
                className={`w-full max-w-[520px] rounded-xl border ${resultConfig.border} ${resultConfig.bg} ${resultConfig.text} p-3 flex items-center gap-3 ${
                  inline
                    ? 'md:absolute md:right-[60px] md:top-[150px] md:z-20 md:w-[360px] md:max-w-none md:min-h-[250px] md:flex-col md:items-center md:justify-center md:text-center md:gap-3 md:p-7 md:shadow-lg'
                    : ''
                }`}>
                <div className="flex items-center justify-center gap-3">
                  <span className="hidden md:inline text-amber-400 text-2xl leading-none" aria-hidden="true">✦</span>
                  <resultConfig.Icon className={`w-6 h-6 shrink-0 ${inline ? 'md:w-12 md:h-12' : ''}`} />
                  <span className="hidden md:inline text-amber-400 text-2xl leading-none" aria-hidden="true">✦</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    data-testid="pc-result-title"
                    className={`font-display font-extrabold text-base leading-tight ${inline ? 'md:text-2xl' : ''}`}
                  >
                    {resultConfig.title}
                  </div>
                  <div
                    data-testid="pc-result-sub"
                    className={`text-xs mt-0.5 ${inline ? 'md:text-sm md:mt-2' : ''}`}
                  >
                    {resultConfig.sub}
                  </div>
                  {resultConfig.extra && (
                    <div
                      data-testid="pc-result-points"
                      className={`text-sm font-bold mt-1 ${inline ? 'md:text-xl md:mt-4' : ''}`}
                    >
                      {resultConfig.extra}
                    </div>
                  )}
                  {isStudent && result && !completed && attemptsLeft > 0 && (
                    <div data-testid="pc-remaining" className="text-[11px] mt-1 opacity-80">Te quedan {attemptsLeft} intento{attemptsLeft === 1 ? '' : 's'}.</div>
                  )}
                  {isStudent && result && !completed && attemptsLeft === 0 && (
                    <div data-testid="pc-exhausted" className="text-[11px] mt-1 opacity-80">Se agotaron tus intentos.</div>
                  )}
                </div>
              </div>
            )}

            {inline && (
              <div
                className="hidden md:flex w-full max-w-[520px] items-center justify-center gap-2 font-display font-extrabold text-lg text-slate-900"
              >
                <Bot className="w-5 h-5 text-indigo-600" />
                <span>{activityTitle}</span>
              </div>
            )}

            <div
              ref={boardWrapRef}
              data-testid="pc-board-wrap"
              className={`w-full max-w-[480px] min-w-0 relative flex justify-center ${inline ? 'mx-auto' : ''}`}
            >
              <ChessBoardView
                fen={fen}
                onPieceDrop={handleDrop}
                onSquareClick={handleSquareClick}
                extraSquareStyles={hintStyles}
                orientation={orientation}
                boardWidth={boardPx}
                arePiecesDraggable={!thinking && engine.ready && !result && !activityLocked}
              />
              {resultConfig?.celebrate && <Confetti />}

              {inline && isStudent && (result === 'checkmate_win' || attemptsExhausted) && (
                <div
                  data-testid="pc-board-reward-popup"
                  className="hidden md:flex absolute inset-0 z-30 items-center justify-center rounded-xl bg-transparent p-6"
                >
                  <div
                    className={`w-full max-w-[340px] rounded-2xl border-2 bg-white/95 p-6 text-center shadow-2xl backdrop-blur-sm ${
                      result === 'checkmate_win'
                        ? 'border-emerald-300'
                        : 'border-amber-300'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-amber-400 text-2xl leading-none" aria-hidden="true">✦</span>
                      <resultConfig.Icon
                        className={`w-12 h-12 ${
                          result === 'checkmate_win'
                            ? 'text-emerald-700'
                            : 'text-amber-500'
                        }`}
                      />
                      <span className="text-amber-400 text-2xl leading-none" aria-hidden="true">✦</span>
                    </div>

                    <div
                      data-testid="pc-board-reward-title"
                      className={`mt-4 font-display font-extrabold text-2xl leading-tight ${
                        result === 'checkmate_win'
                          ? 'text-emerald-800'
                          : 'text-amber-800'
                      }`}
                    >
                      {resultConfig.title}
                    </div>

                    <div
                      data-testid="pc-board-reward-sub"
                      className="mt-2 text-sm leading-relaxed text-slate-600"
                    >
                      {resultConfig.sub}
                    </div>

                    {result === 'checkmate_win' && resultConfig.extra && (
                      <div
                        data-testid="pc-board-reward-points"
                        className="mt-5 font-display font-extrabold text-xl text-emerald-800"
                      >
                        ⭐ {resultConfig.extra.replace('¡', '').replace('!', '')} ⭐
                      </div>
                    )}

                    {attemptsExhausted && result !== 'checkmate_win' && (
                      <div
                        data-testid="pc-board-reward-motivation"
                        className="mt-5 font-display font-extrabold text-lg text-amber-800"
                      >
                        Cada partida te hace mejor.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="text-[11px] font-mono text-slate-400 truncate w-full text-center" data-testid="pc-fen">{fen}</div>
          </div>
  );

  // PC-5E: Modo INLINE — se usa cuando el runner se abre dentro de Classroom
  // (LivePuzzle). Sin Dialog/overlay para que las piezas sean arrastrables y
  // clicables normalmente. StudyDetail mantiene su Dialog intacto.
  if (inline) {
    return (
      <div data-testid="play-computer-inline" className="w-full max-w-full relative">
        <div className="mb-2 w-full max-w-[520px] mx-auto md:absolute md:left-4 md:top-0 md:max-w-none md:w-auto md:mx-0">
          <div className="flex items-center justify-between gap-3 md:hidden">
            <div data-testid="pc-title" className="flex items-center gap-2 font-display font-extrabold text-lg text-slate-900 min-w-0">
              <Bot className="w-5 h-5 text-indigo-600 shrink-0" />
              <span className="truncate">{activityTitle}</span>
            </div>

            {isStudent && (
              <span
                className="shrink-0 inline-flex items-center gap-1 text-xs text-slate-700"
                data-testid="pc-attempts-mobile"
              >
                <Repeat className="w-3.5 h-3.5" />
                Intentos: <b>{Math.min(attemptsUsed, maxAttempts)}/{maxAttempts}</b>
              </span>
            )}
          </div>

          {/* Información del reto — PC solamente. Mobile conserva el layout actual. */}
          <div className="hidden md:block w-[360px] md:translate-y-[56px] md:-translate-x-[24px] text-center">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
              Objetivo
            </div>

            <div className="mt-0.5 flex items-center justify-center gap-2 font-display font-extrabold text-2xl text-slate-900">
              <Trophy className="w-6 h-6 text-amber-500" />
              <span>{goalLabel}</span>
            </div>

            <div className="mt-4 flex items-center justify-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                ♙ {sideLabel}
              </span>

              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                <Bot className="w-3.5 h-3.5 text-indigo-600" />
                Motor {level}
              </span>

              {points ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                  <Coins className="w-3.5 h-3.5" />
                  {points} puntos
                </span>
              ) : null}
            </div>
          </div>

          <div className="md:hidden text-xs text-slate-500 mt-0.5">
            Objetivo: <b>{goalLabel}</b> · Juegas: <b>{sideLabel}</b> · Nivel motor: <b>{level}</b>
            {points ? <> · Puntos: <b data-testid="pc-points">{points}</b></> : null}
          </div>
        </div>

        {body}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="play-computer-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle data-testid="pc-title" className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-indigo-600" />
            {activityTitle}
          </DialogTitle>
          <DialogDescription>
            Objetivo: <b>{goalLabel}</b> · Juegas: <b>{sideLabel}</b> · Nivel motor: <b>{level}</b>
            {points ? <> · Puntos: <b data-testid="pc-points">{points}</b></> : null}
          </DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
